import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import AboutUs from './AboutUs';

function HomePage() {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState({
        bike24: [],
        bikeComponents: [],
        bikeDiscount: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [searchVersion, setSearchVersion] = useState(0);
    const [similarAvailable, setSimilarAvailable] = useState(false);
    const activeRequest = useRef(null);

    // Toggle checkboxes for shops
    const [enabledShops, setEnabledShops] = useState({
        bike24: true,
        bikeComponents: true,
        bikeDiscount: true,
    });

    useEffect(() => {
        axios.get('/api/config').then(response => setSimilarAvailable(response.data.similarAvailable)).catch(() => {});
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        activeRequest.current = controller;
        const searchQuery = query.trim();
        if (!searchQuery) {
            setResults({ bike24: [], bikeComponents: [], bikeDiscount: [] });
            setLoading(false);
            setSubmitted(false);
            setError('');
            return () => controller.abort();
        }
        setLoading(true);
        setError('');
        setSubmitted(true);
        const timer = setTimeout(() => {
        axios.get('/api/products/search', { params: { q: searchQuery }, signal: controller.signal, timeout: 20000 })
            .then(response => { if (!controller.signal.aborted) setResults(response.data.results); })
            .catch(error => {
                if (!controller.signal.aborted) {
                    setError(error.response?.data?.error || 'Search is temporarily unavailable. Please try again.');
                    setResults({ bike24: [], bikeComponents: [], bikeDiscount: [] });
                }
            })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        }, 300);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [query, searchVersion]);

    const submitSearch = event => {
        event.preventDefault();
        activeRequest.current?.abort();
        setSearchVersion(value => value + 1);
    };

    const handleCheckbox = (shop) => {
        setEnabledShops(prev => ({
            ...prev,
            [shop]: !prev[shop],
        }));
    };

    const filteredResults = {
        bike24: enabledShops.bike24 ? results.bike24 : [],
        bikeComponents: enabledShops.bikeComponents ? results.bikeComponents : [],
        bikeDiscount: enabledShops.bikeDiscount ? results.bikeDiscount : []
    };

    return (
        <div className="App">
            <header className="app-header">
                <h1>ShredSaver</h1>
                <p>EU Bicycle Price Terminal // 03 Sources</p>
            </header>

            <div className="search-container">
                <form className="search-bar" onSubmit={submitSearch}>
                    <input
                        value={query}
                        onChange={e => { activeRequest.current?.abort(); setQuery(e.target.value); }}
                        aria-label="Search bike products"
                        maxLength={200}
                        placeholder="Search for bike parts..."
                        className="search-input"
                    />
                    <button type="submit" className="search-button">
                        Search
                    </button>
                </form>

                <div className="search-controls">
                    <div className="shop-filters">
                        <label>
                            <input
                                type="checkbox"
                                checked={enabledShops.bikeComponents}
                                onChange={() => handleCheckbox('bikeComponents')}
                            />
                            Bike-Components
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={enabledShops.bikeDiscount}
                                onChange={() => handleCheckbox('bikeDiscount')}
                            />
                            Bike-Discount
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={enabledShops.bike24}
                                onChange={() => handleCheckbox('bike24')}
                            />
                            Bike24
                        </label>
                    </div>
                </div>
            </div>

            {loading && <div className="loading" role="status">Loading...</div>}
            {error && <p role="alert">{error}</p>}

            <div className="shops-container">
                    {enabledShops.bikeComponents && (
                        <div className="shop-section">
                            <h2>Bike-Components</h2>
                            <div className="shop-grid">
                                {filteredResults.bikeComponents.length
                                    ? filteredResults.bikeComponents.map(item => <ProductCard key={`bc-${item.id}`} product={item} similarAvailable={similarAvailable} shop="bike_components" />)
                                    : <div className="no-results">{error || loading ? '' : submitted ? 'No fresh offers found' : 'Search for a bike part to begin'}</div>
                                }
                            </div>
                        </div>
                    )}

                    {enabledShops.bikeDiscount && (
                        <div className="shop-section">
                            <h2>Bike-Discount</h2>
                            <div className="shop-grid">
                                {filteredResults.bikeDiscount.length
                                    ? filteredResults.bikeDiscount.map(item => <ProductCard key={`bd-${item.id}`} product={item} similarAvailable={similarAvailable} shop="bike_discount" />)
                                    : <div className="no-results">{error || loading ? '' : submitted ? 'No fresh offers found' : 'Search for a bike part to begin'}</div>
                                }
                            </div>
                        </div>
                    )}

                    {enabledShops.bike24 && (
                        <div className="shop-section">
                            <h2>Bike24</h2>
                            <div className="shop-grid">
                                {filteredResults.bike24.length
                                    ? filteredResults.bike24.map(item => <ProductCard key={`b24-${item.id}`} product={item} similarAvailable={similarAvailable} shop="bike24" />)
                                    : <div className="no-results">{error || loading ? '' : submitted ? 'No fresh offers found' : 'Search for a bike part to begin'}</div>
                                }
                            </div>
                        </div>
                    )}
                </div>

            <footer className="app-footer">
                <Link to="/aboutus" className="footer-link">About Us</Link>
            </footer>
        </div>
    );
}

function ProductCard({ product, shop, similarAvailable }) {
    const [suggestions, setSuggestions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const controller = useRef(null);
    useEffect(() => () => controller.current?.abort(), []);
    const findSimilar = async () => {
        controller.current?.abort();
        const request = new AbortController();
        controller.current = request;
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`/api/products/similar/${shop}/${product.id}`, { signal: request.signal, timeout: 30000 });
            if (!request.signal.aborted) setSuggestions(response.data.similar);
        } catch (error) {
            if (!request.signal.aborted) setError(error.response?.data?.error || 'Similar products are temporarily unavailable.');
        } finally { if (!request.signal.aborted) setLoading(false); }
    };
    const names = { bike24: 'Bike24', bike_components: 'Bike-Components', bike_discount: 'Bike-Discount' };
    const formatPrice = value => new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(value);
    return (
        <div className="product-card-container">
            <a className="card" href={product.url} target="_blank" rel="noreferrer">
                <div className="card-image"><img src={product.img} alt={product.title} loading="lazy" /></div>
                <div className="card-info">
                    <h3 className="product-title">{product.title}</h3>
                    {product.brand && <p className="product-brand">{product.brand}</p>}
                    <p className="product-price">{formatPrice(product.price)}</p>
                    {product.scraped_at && <small>Checked {new Date(product.scraped_at).toLocaleDateString()}</small>}
                </div>
            </a>
            {similarAvailable && <button className="compare-button" onClick={findSimilar} disabled={loading}>
                {loading ? 'Loading...' : 'Find similar products'}
            </button>}
            {error && <p role="alert">{error}</p>}
            {suggestions && <div className="similar-products">
                <p>Similar products — check size and specifications before buying. Delivery costs are not included.</p>
                {suggestions.length === 0 && <p>No similar products found.</p>}
                {suggestions.map(item => <a className="similar-product-item" key={`${item.shop}:${item.url}`} href={item.url} target="_blank" rel="noreferrer">
                    <span>{names[item.shop]}: {item.title}</span><strong>{formatPrice(item.price)}</strong>
                </a>)}
            </div>}
        </div>
    );
}

function App() {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/aboutus" element={<AboutUs />} />
            </Routes>
        </Router>
    );
}

export default App;
